import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useFetch } from '../hooks/useFecth';
import Button from '@mui/material/Button';
import Add from '@mui/icons-material/Add';

/**
 * Select de categorias pai + filhas (e netas) com botão "Adicionar".
 * Traz filhas diretas e netas (filhas das filhas) do pai selecionado. Monta a lista no front e envia via onChange; mesmo contrato do SelectCategories (listCategoryId).
 *
 * Props:
 *   value   - array de ids já escolhidos (controlado pelo form)
 *   name    - nome do campo (ex: "listCategoryId")
 *   onChange(name, newArray) - chamado ao adicionar ou remover
 *   label   - label do select de filhas/netas
 */
export default function SelectCategoriesParentChild({ value, name, onChange, label }) {
    const { data, error, isFetching } = useFetch('category', { params: { status: true, all: true } });
    const [parentId, setParentId] = useState(null);
    const [selectedChildren, setSelectedChildren] = useState([]);

    /** IDs sempre como número — evita falha em includes() vs option.id (API / Formik podem misturar tipos). */
    const currentValue = useMemo(() => {
        if (!Array.isArray(value)) return [];
        return value.map(v => Number(v)).filter(n => !Number.isNaN(n));
    }, [value]);
    // API retorna categorias com id, name, childOf (null = pai; número = id do pai).
    const parents = useMemo(() => (data || []).filter(c => !c.childOf), [data]);
    /** Filhas diretas + netas (filhas das filhas) do pai selecionado, para o dropdown. */
    const children = useMemo(() => {
        if (!data?.length || !parentId) return [];
        const direct = (data || []).filter(c => c.childOf === parentId);
        const directIds = new Set(direct.map(c => c.id));
        const grandchild = (data || []).filter(c => c.childOf != null && directIds.has(c.childOf));
        return [...direct, ...grandchild];
    }, [data, parentId]);
    const selectedParent = parents.find(p => p.id === parentId) || null;

    const handleParentChange = (_, newParent) => {
        setParentId(newParent?.id ?? null);
        setSelectedChildren([]);
    };

    /** Adiciona pai + filhas selecionadas ao array; evita duplicatas e limpa a seleção de filhas. */
    const handleAdd = () => {
        if (!parentId) return;
        const parentIdNum = Number(parentId);
        const childIds = (selectedChildren || []).map(c => c.id);
        const idsToAdd = [parentIdNum, ...childIds].filter(id => !currentValue.includes(id));
        if (idsToAdd.length === 0) return;
        onChange(name, [...currentValue, ...idsToAdd]);
        setSelectedChildren([]);
    };

    const handleRemove = idToRemove => {
        onChange(name, currentValue.filter(id => id !== idToRemove));
    };

    /** Agrupa por pai (só raiz); filhos = filhas + netas em lista, todos em array flat. Apenas o PAI é destacado. */
    const groupedByParent = useMemo(() => {
        if (!data?.length || !currentValue.length) return [];
        const parentCats = (data || []).filter(c => !c.childOf);
        return parentCats
            .map(parent => {
                const filhasIds = new Set((data || []).filter(c => c.childOf === parent.id).map(c => c.id));
                const filhosInList = (data || []).filter(
                    c =>
                        currentValue.includes(c.id) &&
                        (c.childOf === parent.id || (c.childOf != null && filhasIds.has(c.childOf))),
                );
                const parentInList = currentValue.includes(parent.id);
                if (!parentInList && filhosInList.length === 0) return null;
                return {
                    parent: { id: parent.id, name: parent.name },
                    parentInList,
                    children: filhosInList.map(c => ({ id: c.id, name: c.name })),
                };
            })
            .filter(Boolean);
    }, [data, currentValue]);

    return (
        <Stack spacing={2}>
            {/* Select categoria pai (única); ao trocar, limpa a seleção de filhas. */}
            <Autocomplete
                getOptionLabel={o => o?.name ?? ''}
                options={parents}
                value={selectedParent}
                onChange={handleParentChange}
                loading={isFetching}
                renderInput={params => <TextField {...params} label="Categoria pai" />}
            />
            {/* Select filhas + netas (múltiplo): do pai atual; já adicionadas ficam desabilitadas e destacadas. */}
            <Autocomplete
                multiple
                getOptionLabel={o => o?.name ?? ''}
                getOptionDisabled={option => currentValue.includes(option.id)}
                options={children}
                value={selectedChildren}
                onChange={(_, newVal) => setSelectedChildren(newVal || [])}
                loading={isFetching}
                disabled={!parentId}
                renderInput={params => (
                    <TextField {...params} label={label || 'Categorias (filhas)'} />
                )}
                renderOption={(props, option, { selected }) => {
                    const alreadyInList = currentValue.includes(option.id);
                    return (
                        <li
                            {...props}
                            style={{
                                backgroundColor: alreadyInList
                                    ? 'rgba(76, 175, 80, 0.12)'
                                    : selected
                                        ? '#08fb558e'
                                        : 'inherit',
                            }}
                        >
                            {option.name}
                            {alreadyInList && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    (já adicionada)
                                </Typography>
                            )}
                        </li>
                    );
                }}
            />
            <Button
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={handleAdd}
                disabled={!parentId}
            >
                Adicionar seleção
            </Button>

            {/* Listagem: só o PAI (raiz) destacado; filhas e netas como chips normais. */}
            {groupedByParent.length > 0 && (
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Categorias escolhidas
                    </Typography>
                    <Stack spacing={1.5}>
                        {groupedByParent.map(({ parent, parentInList, children: childList }) => (
                            <Stack key={parent.id} spacing={0.5}>
                                {parentInList ? (
                                    <Chip
                                        label={parent.name}
                                        size="small"
                                        onDelete={() => handleRemove(parent.id)}
                                        sx={{ fontWeight: 600, bgcolor: 'action.hover' }}
                                    />
                                ) : (
                                    childList.length > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                                            {parent.name}
                                        </Typography>
                                    )
                                )}
                                {childList.length > 0 && (
                                    <Stack direction="row" flexWrap="wrap" gap={0.5} pl={parentInList ? 2 : 0}>
                                        {childList.map(({ id, name: catName }) => (
                                            <Chip
                                                key={id}
                                                label={catName}
                                                size="small"
                                                onDelete={() => handleRemove(id)}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            )}

            {!!error?.message && <FormHelperText error>{error?.message}</FormHelperText>}
        </Stack>
    );
}
